import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '@nha/api/shared';
import { IBusinessApplication } from '@nha/api/core';
import { NHASnackbarService } from '@nha/components/snack-bar';
import { NotificationsApiConfiguration } from '../notifications-api-configuration.class';
import { INotificationFrequency, INotificationType, IUserNotification, IUserNotificationTypeSetting } from '../interfaces';

/**
 * Current-user view of the Notifications API (UserNotificationsController). Every route derives
 * the user from the bearer token, so no method takes a user ID.
 */
@Injectable({ providedIn: 'root' })
export class UserNotificationService extends HttpService {
	constructor() {
		const config = inject(NotificationsApiConfiguration);
		const snackbarService = inject(NHASnackbarService);

		super(config, 'UserNotifications', snackbarService);
	}

	/**
	 * `quiet` drops the error snackbar for reloads the user never asked for — a realtime push or
	 * a reconnect re-sync shouldn't interrupt them with a failure they can't act on.
	 */
	getNotifications(businessSystemID: number, skip: number, take: number, quiet: boolean = false): Observable<IUserNotification[]> {
		return this.get<IUserNotification[]>(this.serviceUrl, {
			params: { businessSystemID, skip, take },
			snackbarErrorMessage: quiet ? undefined : 'Failed to load notifications.'
		});
	}

	markAsRead(notificationRecipientID: number): Observable<void> {
		return this.put<void>(`${this.serviceUrl}/mark-as-read/${notificationRecipientID}`, null, {
			snackbarErrorMessage: 'Failed to mark the notification as read.'
		});
	}

	markAllAsRead(businessSystemID: number): Observable<void> {
		return this.put<void>(`${this.serviceUrl}/mark-all-as-read`, null, {
			params: { businessSystemID },
			snackbarErrorMessage: 'Failed to mark all notifications as read.'
		});
	}

	getBusinessApplications(businessSystemID: number): Observable<IBusinessApplication[]> {
		return this.get<IBusinessApplication[]>(`${this.serviceUrl}/applications`, {
			params: { businessSystemID },
			snackbarErrorMessage: 'Failed to load notification applications.'
		});
	}

	getNotificationTypes(businessSystemID: number): Observable<INotificationType[]> {
		return this.get<INotificationType[]>(`${this.serviceUrl}/types`, {
			params: { businessSystemID },
			snackbarErrorMessage: 'Failed to load notification types.'
		});
	}

	getNotificationFrequencies(): Observable<INotificationFrequency[]> {
		return this.get<INotificationFrequency[]>(`${this.serviceUrl}/frequencies`, {
			snackbarErrorMessage: 'Failed to load notification frequencies.'
		});
	}

	getSettings(businessSystemID: number): Observable<IUserNotificationTypeSetting[]> {
		return this.get<IUserNotificationTypeSetting[]>(`${this.serviceUrl}/settings`, {
			params: { businessSystemID },
			snackbarErrorMessage: 'Failed to load notification settings.'
		});
	}

	saveSettings(settings: IUserNotificationTypeSetting[]): Observable<void> {
		return this.post<void>(`${this.serviceUrl}/settings`, settings, {
			snackbarErrorMessage: 'Failed to save notification settings.'
		});
	}

	/**
	 * Stores a real Global Test notification for the current user, so the whole path can be checked
	 * from the bell: creation, channel eligibility, delivery and the in-app signal. Muting the
	 * Global Test type therefore silences this too — nothing is stored, so nothing is pushed.
	 * Requires the Core:Feature|Admin:Access entitlement — enforced server-side; callers should
	 * hide the affordance behind the same check.
	 */
	sendTestNotification(): Observable<void> {
		return this.post<void>(`${this.serviceUrl}/broadcast-test`, null, {
			snackbarErrorMessage: 'Failed to send the test notification.'
		});
	}
}
